import { CohortType, Cohort } from "encamp-prisma/generated/client";

/**
 * Minimal cohort type needed for identifier operations
 */
export interface CohortIdentifierData {
  id: string;
  type: CohortType;
  state?: string | null;
  fdId?: string | null;
  lepcId?: string | null;
  credentialUsername?: string | null;
  alternateIdValue?: string | null;
}

/**
 * Map of cohort types to their corresponding identifier field in the Cohort model
 */
export const cohortIdentifierMap: Map<CohortType, keyof CohortIdentifierData> =
  new Map([
    [CohortType.LEPC, "lepcId"],
    [CohortType.CREDENTIAL, "credentialUsername"],
    [CohortType.ALTERNATE_ID, "alternateIdValue"],
    [CohortType.STATE, "state"],
    [CohortType.FIRE_DEPARTMENT, "fdId"],
  ]);

/**
 * Get the identifier value for a cohort based on its type
 * @param cohort The cohort to get the identifier for
 * @returns The identifier value as a string
 * @throws Error if cohort type is missing or unknown
 */
export function getCohortIdentifier(cohort: Cohort): string {
  if (!cohort.type) {
    throw new Error("Cohort type is required");
  }

  switch (cohort.type) {
    case CohortType.STATE:
      return cohort.state ?? "";
    case CohortType.FIRE_DEPARTMENT:
      return cohort.fdId ?? "";
    case CohortType.LEPC:
      return cohort.lepcId ?? "";
    case CohortType.CREDENTIAL:
      return cohort.credentialUsername ?? "";
    case CohortType.ALTERNATE_ID:
      return cohort.alternateIdValue ?? "";
    default:
      throw new Error(`Unknown cohort type: ${cohort.type}`);
  }
}

/**
 * Get both the identifier key and identifier value for a cohort
 * @param cohort The cohort to get the identifier information for
 * @returns An object containing the identifier key and identifier value
 * @throws Error if cohort type is invalid or identifier value is empty
 */
export function getIdentifierKeyAndIdentifierFromCohort({
  cohort,
}: {
  cohort: CohortIdentifierData;
}): { identifierKey: keyof CohortIdentifierData; identifier: string } {
  const identifierKey = cohortIdentifierMap.get(cohort.type);
  if (!identifierKey) {
    throw new Error("Invalid cohort type");
  }

  if (!cohort[identifierKey]) {
    throw new Error(
      `Cohort ${cohort.id} has an empty ${identifierKey} value for cohort type ${cohort.type}`
    );
  }

  return { identifierKey, identifier: String(cohort[identifierKey]) };
}
